/**
 * The Directory and Locations pages' arithmetic, kept pure and shared: the
 * backend runs it once per provider snapshot to answer paged and searched
 * reads (`api/projectDetailsRoutes.ts`), and the console runs it on a file it
 * is reviewing before upload, so both agree on who belongs to which company,
 * the crew bar's segments, and the location hierarchy's shape.
 *
 * Drawn from `Led AI - Project Details.dc.html` (project `806e74fa`). The
 * artboard's colours live here because they are DATA — a company's colour is
 * decided by its rank, and the bar, the legend, the avatars and the
 * companies table must all agree on it.
 */
import type {
  ProjectDetailsDirectory,
  ProjectDetailsLocation,
  ProjectDetailsPerson,
} from "./projectDetails";

/** The six largest companies, in rank order, and the ink that reads on each. */
export const TOP_FILL = ["#F6BE18", "#D98A0B", "#00875A", "#3A7CA5", "#2E7F7F", "#8E6232"];
export const TOP_FG = ["#0A0A0A", "#0A0A0A", "#fff", "#fff", "#fff", "#fff"];
export const REST_FILL = "#C9C1B1";
export const OTHER_FILL = "#E4DDCF";
/** "No company" — hatched, so it never reads as one more company. */
export const NONE_FILL = "repeating-linear-gradient(135deg,#D9D2C4 0 3px,#EFE9DE 3px 6px)";
export const BRANCH_FILL = ["#F6BE18", "#D98A0B", "#00875A", "#3A7CA5", "#2E7F7F", "#8E6232", "#4F6D7A", "#D14A32", "#C9C1B1"];
export const TOP_COUNT = 6;

export interface CompanyGroup {
  id: string;
  name: string;
  trade?: string;
  count: number;
  /** The two most common roles, "Electrician · Apprentice", or "—". */
  roles: string;
  fill: string;
  fg: string;
  top: boolean;
}

export interface CrewSegment {
  /** A company id, `"other"` or `"none"`. */
  id: string;
  name: string;
  short: string;
  fill: string;
  count: number;
}

export interface DirectoryModel {
  companies: CompanyGroup[];
  /** Every company with at least one person, largest first, then the rest. */
  crew: CrewSegment[];
  people: Array<ProjectDetailsPerson & { groupId: string | null; bucket: string }>;
  noCompany: number;
  missingInfo: number;
}

const nameKey = (name: string) => "name:" + name.trim().toLowerCase();

/**
 * Group people under companies.
 *
 * A person joins a company by id when the provider links them, and by name
 * when it only gave a name — a Procore user whose vendor is not on the
 * project's Vendors list still says which company they work for, and that is
 * a company on this page even though no company row named it.
 */
export function buildDirectory(directory: ProjectDetailsDirectory): DirectoryModel {
  const groups = new Map<string, { id: string; name: string; trade?: string; roles: Map<string, number>; count: number }>();
  const byName = new Map<string, string>();
  for (const company of directory.companies) {
    groups.set(company.id, { id: company.id, name: company.name, ...(company.trade ? { trade: company.trade } : {}), roles: new Map(), count: 0 });
    byName.set(nameKey(company.name), company.id);
  }
  const placed = directory.people.map((person) => {
    let groupId: string | null = null;
    if (person.companyId && groups.has(person.companyId)) groupId = person.companyId;
    else if (person.company?.trim()) {
      groupId = byName.get(nameKey(person.company)) ?? nameKey(person.company);
      if (!groups.has(groupId)) {
        groups.set(groupId, { id: groupId, name: person.company.trim(), roles: new Map(), count: 0 });
        byName.set(groupId, groupId);
      }
    }
    if (groupId) {
      const group = groups.get(groupId)!;
      group.count += 1;
      if (person.role) group.roles.set(person.role, (group.roles.get(person.role) ?? 0) + 1);
    }
    return { ...person, groupId };
  });

  const companies = rankCompanies(
    [...groups.values()].map((group) => ({
      id: group.id,
      name: group.name,
      ...(group.trade ? { trade: group.trade } : {}),
      count: group.count,
      roles: [...group.roles.entries()].sort((a, b) => b[1] - a[1]).slice(0, 2).map(([role]) => role).join(" · ") || "—",
    })),
  );
  const topIds = new Set(companies.filter((company) => company.top).map((company) => company.id));
  const noCompany = placed.filter((person) => !person.groupId).length;
  const crew = crewSegments(companies, noCompany);

  const people = placed
    .map((person) => ({
      ...person,
      bucket: !person.groupId ? "none" : topIds.has(person.groupId) ? person.groupId : "other",
    }))
    .sort((a, b) => lastName(a.name).localeCompare(lastName(b.name)) || a.name.localeCompare(b.name));

  return {
    companies,
    crew,
    people,
    noCompany,
    missingInfo: placed.filter((person) => !person.groupId || !person.role).length,
  };
}

/** Rank companies largest first and give the top six their colours. Run on
 *  the server's counts too, so a page that never holds the whole directory
 *  colours it exactly as the whole directory would. */
export function rankCompanies(
  counted: Array<{ id: string; name: string; trade?: string; count: number; roles: string }>,
): CompanyGroup[] {
  return [...counted]
    .sort((a, b) => b.count - a.count || a.name.localeCompare(b.name))
    .map((company, index) => {
      const top = index < TOP_COUNT && company.count > 0;
      return {
        ...company,
        fill: top ? TOP_FILL[index]! : REST_FILL,
        fg: top ? TOP_FG[index]! : "#6B665D",
        top,
      };
    });
}

/** The crew bar: the top six, everyone else as one segment, then no company. */
export function crewSegments(companies: CompanyGroup[], noCompany: number): CrewSegment[] {
  const rest = companies.filter((company) => !company.top && company.count > 0);
  const restCount = rest.reduce((sum, company) => sum + company.count, 0);
  const crew: CrewSegment[] = companies
    .filter((company) => company.top)
    .map((company) => ({ id: company.id, name: company.name, short: company.name, fill: company.fill, count: company.count }));
  if (restCount) {
    crew.push({
      id: "other",
      name: `${rest.length} other ${rest.length === 1 ? "company" : "companies"}`,
      short: `${rest.length} ${rest.length === 1 ? "other" : "others"}`,
      fill: OTHER_FILL,
      count: restCount,
    });
  }
  if (noCompany) crew.push({ id: "none", name: "No company", short: "No company", fill: NONE_FILL, count: noCompany });
  return crew;
}

/** Which crew-bar segment a person falls in. */
export function bucketOf(groupId: string | null, companies: CompanyGroup[]): string {
  if (!groupId) return "none";
  return companies.find((company) => company.id === groupId)?.top ? groupId : "other";
}

export function lastName(name: string): string {
  const parts = name.trim().split(/\s+/);
  return parts[parts.length - 1] ?? "";
}

export function initials(name: string): string {
  return name
    .replace(/[^\p{L}\p{N}& ]/gu, "")
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((word) => word[0])
    .join("")
    .toUpperCase();
}

export function matchesPerson(person: ProjectDetailsPerson, query: string): boolean {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  return [person.name, person.role, person.company, person.email].some((value) => value?.toLowerCase().includes(q));
}

// ---------------------------------------------------------------------------
// Locations
// ---------------------------------------------------------------------------

export interface LocationNode {
  id: string;
  name: string;
  /** 1 for a top-level location. */
  depth: number;
  parentId: string | null;
  children: string[];
  /** Everything beneath it, at any depth. */
  descendants: number;
}

export interface LocationTree {
  byId: Map<string, LocationNode>;
  roots: string[];
  total: number;
  maxDepth: number;
}

/**
 * The hierarchy, from flat `{ id, parentId }` rows.
 *
 * Defensive about the input on purpose: a parent that is not in the list makes
 * its child a root rather than dropping it, and a cycle (which no provider
 * should send, but a cycle would hang the page) is broken where it closes.
 */
export function buildLocationTree(rows: ProjectDetailsLocation[]): LocationTree {
  const byId = new Map<string, LocationNode>();
  for (const row of rows) {
    if (!byId.has(row.id)) byId.set(row.id, { id: row.id, name: row.name, depth: 0, parentId: row.parentId ?? null, children: [], descendants: 0 });
  }
  for (const node of byId.values()) {
    if (node.parentId && (!byId.has(node.parentId) || node.parentId === node.id)) node.parentId = null;
  }
  // Break cycles: walk up from each node; a node met twice closes a loop.
  for (const node of byId.values()) {
    const seen = new Set<string>([node.id]);
    let current = node;
    while (current.parentId) {
      if (seen.has(current.parentId)) {
        current.parentId = null;
        break;
      }
      seen.add(current.parentId);
      current = byId.get(current.parentId)!;
    }
  }
  const roots: string[] = [];
  for (const node of byId.values()) {
    if (node.parentId) byId.get(node.parentId)!.children.push(node.id);
    else roots.push(node.id);
  }
  let maxDepth = 0;
  const visit = (id: string, depth: number): number => {
    const node = byId.get(id)!;
    node.depth = depth;
    maxDepth = Math.max(maxDepth, depth);
    let count = 0;
    for (const child of node.children) count += 1 + visit(child, depth + 1);
    node.descendants = count;
    return count;
  };
  for (const root of roots) visit(root, 1);
  return { byId, roots, total: byId.size, maxDepth };
}

/** Root-first ids from `id` up to its top-level ancestor, `id` last. */
export function pathTo(tree: LocationTree, id: string): string[] {
  const out: string[] = [];
  let node = tree.byId.get(id);
  while (node) {
    out.unshift(node.id);
    node = node.parentId ? tree.byId.get(node.parentId) : undefined;
  }
  return out;
}

/** How many levels lie below `id` (0 for a leaf). */
export function depthBelow(tree: LocationTree, id: string): number {
  const node = tree.byId.get(id);
  if (!node) return 0;
  let deepest = 0;
  const stack = [...node.children];
  while (stack.length) {
    const next = tree.byId.get(stack.pop()!)!;
    deepest = Math.max(deepest, next.depth - node.depth);
    stack.push(...next.children);
  }
  return deepest;
}

export interface VisibleRow {
  node: LocationNode;
  /** Depth relative to the scope (0 = directly inside it). */
  rel: number;
  open: boolean;
}

/**
 * The rows the tree draws: the scope's subtree, expanded where `open` says.
 * A search shows every match AND the ancestors that lead to it, all open —
 * a hit three levels down is useless without the path to it.
 */
export function visibleRows(
  tree: LocationTree,
  scopeId: string | null,
  open: ReadonlySet<string>,
  query: string,
): VisibleRow[] {
  const q = query.trim().toLowerCase();
  let match: Set<string> | null = null;
  if (q) {
    match = new Set();
    for (const node of tree.byId.values()) {
      if (node.name.toLowerCase().includes(q)) for (const id of pathTo(tree, node.id)) match.add(id);
    }
  }
  const baseDepth = scopeId ? tree.byId.get(scopeId)!.depth : 0;
  const rows: VisibleRow[] = [];
  const walk = (ids: string[]) => {
    for (const id of ids) {
      if (match && !match.has(id)) continue;
      const node = tree.byId.get(id)!;
      const isOpen = match ? true : open.has(id);
      rows.push({ node, rel: node.depth - baseDepth - 1, open: isOpen });
      if (isOpen) walk(node.children);
    }
  };
  walk(scopeId ? tree.byId.get(scopeId)!.children : tree.roots);
  return rows;
}

export function levelLabel(depth: number): string {
  return ["Building / area", "Floor / zone", "Room / unit", "Space", "Sub-space", "Detail"][depth - 1] ?? `Level ${depth}`;
}
